'use client';

import { getSafeRedirectTarget } from '@/features/auth/utils/authRedirect';
import { useCallback, useEffect, useRef, useState } from 'react';

const POPUP_RESULT_TYPE = 'dgh:oauth-result';
const POPUP_RESULT_STORAGE_KEY_PREFIX = 'dgh:oauth-result:';
const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 720;
const POPUP_RESULT_GRACE_MS = 15000;

function getPopupResultStorageKey(provider) {
  return `${POPUP_RESULT_STORAGE_KEY_PREFIX}${provider}`;
}

function buildPopupFeatures(width, height) {
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const currentWidth =
    window.innerWidth || document.documentElement.clientWidth || screen.width;
  const currentHeight =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    screen.height;
  const left = Math.max(0, dualScreenLeft + (currentWidth - width) / 2);
  const top = Math.max(0, dualScreenTop + (currentHeight - height) / 2);

  return [
    'popup=yes',
    'toolbar=no',
    'menubar=no',
    'location=yes',
    'status=no',
    'scrollbars=yes',
    'resizable=yes',
    `width=${Math.round(width)}`,
    `height=${Math.round(height)}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
  ].join(',');
}

function buildOauthStartUrl(provider, redirectTarget) {
  const safeRedirectTarget = getSafeRedirectTarget(redirectTarget, '/account');
  const params = new URLSearchParams({ redirect: safeRedirectTarget });

  if (typeof window !== 'undefined') {
    params.set(
      'callbackUrl',
      new URL(
        `/api/users/auth/oauth/${provider}/callback`,
        window.location.origin,
      ).toString(),
    );
  }

  return `/api/users/auth/oauth/${provider}/start?${params.toString()}`;
}

export function useOAuthPopupAuth({
  provider,
  redirectTarget = '/account',
  onSuccess,
  onError,
} = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const redirectTargetRef = useRef(redirectTarget);
  const popupRef = useRef(null);
  const cleanupRef = useRef(() => {});

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    redirectTargetRef.current = redirectTarget;
  }, [onError, onSuccess, redirectTarget]);

  useEffect(
    () => () => {
      cleanupRef.current?.();

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    },
    [],
  );

  const signIn = useCallback(() => {
    if (typeof window === 'undefined') {
      onErrorRef.current?.('Social sign-in is only available in the browser.');
      return;
    }

    cleanupRef.current?.();

    const nextRedirectTarget = getSafeRedirectTarget(
      redirectTargetRef.current,
      '/account',
    );
    const popupResultStorageKey = getPopupResultStorageKey(provider);

    try {
      window.localStorage.removeItem(popupResultStorageKey);
    } catch {
      // Ignore storage access failures and continue with message-based auth.
    }

    const popup = window.open(
      buildOauthStartUrl(provider, nextRedirectTarget),
      `oauth-${provider}`,
      buildPopupFeatures(POPUP_WIDTH, POPUP_HEIGHT),
    );

    if (!popup) {
      onErrorRef.current?.(
        'Authentication popup was blocked. Please allow popups and try again.',
      );
      return;
    }

    popupRef.current = popup;
    popup.focus?.();
    setIsLoading(true);

    let popupCloseTimer = 0;
    let didResolve = false;
    let popupClosedAt = 0;
    let lastProfilePollAt = 0;
    let broadcastChannel = null;

    const cleanup = () => {
      if (popupCloseTimer) {
        window.clearInterval(popupCloseTimer);
      }

      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);

      if (broadcastChannel) {
        try {
          broadcastChannel.close();
        } catch {
          // ignore
        }
        broadcastChannel = null;
      }

      // If auth was resolved, watch for late writes from the popup script for a
      // few seconds. The popup may write to localStorage after we've already
      // resolved via another channel (e.g. profile-poll), and by that point all
      // regular listeners have been torn down. Catch and immediately discard any
      // such write so the stale result doesn't linger until the next logout.
      if (didResolve) {
        const lateWriteHandler = (event) => {
          if (event.key === popupResultStorageKey && event.newValue !== null) {
            try {
              window.localStorage.removeItem(popupResultStorageKey);
            } catch {
              // ignore
            }
          }
        };
        window.addEventListener('storage', lateWriteHandler);
        window.setTimeout(
          () => window.removeEventListener('storage', lateWriteHandler),
          4000,
        );
      }

      cleanupRef.current = () => {};
      setIsLoading(false);
    };

    const readStoredAuthResult = () => {
      try {
        const storedValue = window.localStorage.getItem(popupResultStorageKey);
        if (!storedValue) {
          return null;
        }

        const data = JSON.parse(storedValue);
        if (
          !data ||
          data.type !== POPUP_RESULT_TYPE ||
          data.provider !== provider
        ) {
          return null;
        }

        window.localStorage.removeItem(popupResultStorageKey);
        return data;
      } catch {
        return null;
      }
    };

    const resolveAuthResult = (rawData) => {
      if (didResolve || !rawData) {
        return;
      }

      didResolve = true;

      // Always clear the stored result regardless of which channel fired first
      // (BroadcastChannel, postMessage, and profile-poll don't go through
      // readStoredAuthResult so they would leave the key behind otherwise).
      try {
        window.localStorage.removeItem(popupResultStorageKey);
      } catch {
        // ignore
      }

      cleanup();

      const resolvedRedirectTarget = getSafeRedirectTarget(
        rawData.redirectTarget,
        nextRedirectTarget,
      );

      if (popup && !popup.closed) {
        popup.close();
      }

      if (!rawData.success) {
        onErrorRef.current?.(rawData.message || `${provider} sign-in failed.`);
        return;
      }

      // Exchange the one-time session token for a real JWT cookie.
      // This fetch happens in the OPENER window so the cookie is set
      // correctly here, not in the popup — matching the Google flow.
      const sessionToken = String(rawData.sessionToken || '').trim();
      if (sessionToken) {
        fetch(
          `/api/users/auth/oauth/complete?token=${encodeURIComponent(sessionToken)}`,
          { credentials: 'include', cache: 'no-store' },
        )
          .then((r) => {
            if (!r.ok) {
              return r.json().then((body) => {
                throw new Error(
                  body?.message || `${provider} session exchange failed.`,
                );
              });
            }
            return r.json();
          })
          .then((data) => {
            onSuccessRef.current?.(data, resolvedRedirectTarget);
          })
          .catch((err) => {
            // Session-token exchange failed. Before surfacing the error, check
            // whether generateToken already set the cookie in the popup so the
            // user is in fact logged in.
            fetch('/api/users/profile', {
              credentials: 'include',
              cache: 'no-store',
            })
              .then((r) => {
                if (r.ok) {
                  onSuccessRef.current?.(rawData, resolvedRedirectTarget);
                } else {
                  onErrorRef.current?.(
                    err?.message || `${provider} sign-in could not complete.`,
                  );
                }
              })
              .catch(() => {
                onErrorRef.current?.(
                  err?.message || `${provider} sign-in could not complete.`,
                );
              });
          });
      } else {
        // No session token (legacy / fallback path): fire success directly.
        onSuccessRef.current?.(rawData, resolvedRedirectTarget);
      }
    };

    const handleMessage = (event) => {
      // Accept from our known popup reference (primary check) or from the
      // same origin as a fallback — guards against WindowProxy identity drift
      // across navigations while still blocking cross-origin injection.
      const fromKnownPopup = event.source === popup;
      const fromSameOrigin =
        typeof window !== 'undefined' &&
        event.origin === window.location.origin;
      if (!fromKnownPopup && !fromSameOrigin) {
        return;
      }

      const data = event.data;
      if (
        !data ||
        data.type !== POPUP_RESULT_TYPE ||
        data.provider !== provider
      ) {
        return;
      }

      resolveAuthResult(data);
    };

    const handleStorage = (event) => {
      if (event.key !== popupResultStorageKey || !event.newValue) {
        return;
      }

      const data = readStoredAuthResult();
      if (data) {
        resolveAuthResult(data);
      }
    };

    // BroadcastChannel — most reliable same-origin cross-window mechanism.
    // The popup broadcasts the result on the 'dgh-oauth' channel; we receive
    // it here regardless of postMessage / storage event reliability.
    try {
      broadcastChannel = new BroadcastChannel('dgh-oauth');
      broadcastChannel.onmessage = (event) => {
        const data = event.data;
        if (
          !data ||
          data.type !== POPUP_RESULT_TYPE ||
          data.provider !== provider
        ) {
          return;
        }
        resolveAuthResult(data);
      };
    } catch {
      // BroadcastChannel not supported — fall back to other channels.
    }

    cleanupRef.current = cleanup;
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    try {
      const existingPayload = readStoredAuthResult();
      if (existingPayload) {
        resolveAuthResult(existingPayload);
      }
    } catch {
      // Ignore storage access failures and rely on postMessage only.
    }

    popupCloseTimer = window.setInterval(() => {
      const storedPayload = readStoredAuthResult();
      if (storedPayload) {
        resolveAuthResult(storedPayload);
        return;
      }

      if (!popup || popup.closed) {
        if (!popupClosedAt) {
          popupClosedAt = Date.now();
        }

        const finalStoredPayload = readStoredAuthResult();
        if (finalStoredPayload) {
          resolveAuthResult(finalStoredPayload);
          return;
        }

        // Fallback: poll the profile endpoint every 2 s while we wait.
        // Since generateToken sets the cookie in the popup callback, a 200
        // confirms auth completed even when all signal channels were missed.
        if (!didResolve && Date.now() - lastProfilePollAt >= 1000) {
          lastProfilePollAt = Date.now();
          fetch('/api/users/profile', {
            credentials: 'include',
            cache: 'no-store',
          })
            .then((r) => {
              if (r.ok && !didResolve) {
                resolveAuthResult({
                  type: POPUP_RESULT_TYPE,
                  provider,
                  success: true,
                  redirectTarget: nextRedirectTarget,
                  message: '',
                  sessionToken: '', // cookie already set by generateToken
                });
              }
            })
            .catch(() => {});
        }

        if (Date.now() - popupClosedAt >= POPUP_RESULT_GRACE_MS) {
          // Last chance: read localStorage before tearing down.
          const lastPayload = !didResolve ? readStoredAuthResult() : null;
          if (lastPayload) {
            resolveAuthResult(lastPayload);
            return;
          }
          cleanup();
        }

        return;
      }

      popupClosedAt = 0;
    }, 400);
  }, [provider]);

  return {
    isLoading,
    signIn,
  };
}
